import React from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { OM_LOGO, MTN_LOGO, OPERATOR_COLORS } from "../assets/logos";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { fmt } from "../utils/helpers";

export default function Confirm() {
  const navigate = useNavigate();
  const { pendingTx, addTransaction } = useApp();

  if (!pendingTx) {
    navigate("/");
    return null;
  }

  const { type, payOp, recOp, payPhone, recPhone, amount, fees, total, from } = pendingTx;

  const payCol = OPERATOR_COLORS[payOp];
  const recCol = OPERATOR_COLORS[recOp];
  const isCredit = type === "credit";
  const accentColor = isCredit ? payCol.primary : "#0d0d1a";

  const handleConfirm = () => {
    addTransaction({
      type,
      payPhone,
      recPhone,
      date: pendingTx.date,
      time: pendingTx.time,
      amount: -amount,
      label: pendingTx.label,
    });
    navigate("/success");
  };

  return (
    <div className="bg-[#f0f2f7] pb-2 screen-enter">
      {/* Header */}
      <div className="px-[17px] pt-[14px] pb-[18px]" style={{ background: accentColor }}>
        <button
          onClick={() => navigate(from || "/")}
          className="bg-transparent border-none text-white/75 text-xs cursor-pointer p-0 flex items-center gap-1 mb-[9px] font-medium tap-highlight"
        >
          <i className="fa fa-arrow-left" /> Modifier
        </button>
        <h2 className="text-white text-[17px] font-bold mb-0.5">Confirmation</h2>
        <p className="text-white/70 text-[11px]">Vérifiez les détails avant de confirmer</p>
      </div>

      <div className="p-3">
        {/* Badge type */}
        <div className="flex justify-center mb-3">
          <div
            className="rounded-[18px] px-[15px] py-1.5 flex items-center gap-1.5 border"
            style={{
              background: isCredit ? payCol.light : "#f0f7ff",
              borderColor: isCredit ? payCol.border : "#bfdbf7",
            }}
          >
            <i
              className={`fa ${isCredit ? "fa-mobile-alt" : "fa-exchange-alt"} text-[11px]`}
              style={{ color: isCredit ? payCol.primary : "#005B8E" }}
            />
            <span
              className="text-xs font-bold"
              style={{ color: isCredit ? payCol.primary : "#005B8E" }}
            >
              {isCredit ? "Achat de crédit" : "Transfert Mobile Money"}
            </span>
          </div>
        </div>

        {/* Parties */}
        <Card className="mb-[9px]" style={{ padding: "14px" }}>
          {/* Payeur */}
          <div className="mb-[11px]">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.7px] mb-2">
              {isCredit ? "Payeur" : "Débit"} · {payCol.name}
            </p>
            <div className="flex items-center gap-[9px]">
              <div
                className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0 border"
                style={{ background: payCol.light, borderColor: payCol.border }}
              >
                <img src={payOp === "o" ? OM_LOGO : MTN_LOGO} alt="" style={{ height: payOp === "o" ? 18 : 15 }} />
              </div>
              <div>
                <p className="text-sm font-bold text-brand-dark">+237 {payPhone}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">{payCol.name}</p>
              </div>
            </div>
          </div>

          {/* Séparateur */}
          <div className="flex items-center mb-[11px]">
            <div className="flex-1 h-px bg-gray-100" />
            <div className="w-[22px] h-[22px] rounded-full bg-gray-100 flex items-center justify-center mx-2 flex-shrink-0">
              <i className="fa fa-arrow-down text-gray-500" style={{ fontSize: 8 }} />
            </div>
            <div className="flex-1 h-px bg-gray-100" />
          </div>

          {/* Destinataire */}
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.7px] mb-2">
              {isCredit ? "Destinataire" : "Réception"} · {recCol.nameShort}
            </p>
            <div className="flex items-center gap-[9px]">
              <div
                className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0 border"
                style={{ background: recCol.light, borderColor: recCol.border }}
              >
                <img src={recOp === "o" ? OM_LOGO : MTN_LOGO} alt="" style={{ height: recOp === "o" ? 18 : 15 }} />
              </div>
              <div>
                <p className="text-sm font-bold text-brand-dark">+237 {recPhone}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">{recCol.name}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Récapitulatif */}
        <Card className="mb-[9px] !p-[13px]">
          <div className="flex justify-between mb-1.5">
            <span className="text-xs text-gray-500">Montant</span>
            <span className="text-[13px] font-bold text-brand-dark">{fmt(amount)}</span>
          </div>
          <div className="flex justify-between mb-[9px]">
            <span className="text-xs text-gray-500">Frais de service</span>
            <span className="text-xs font-semibold text-amber-700">{fmt(fees)}</span>
          </div>
          <div className="border-t border-gray-100 pt-[9px] flex justify-between">
            <span className="text-[13px] font-bold text-brand-dark">Total à payer</span>
            <span
              className="text-base font-bold"
              style={{ color: isCredit ? payCol.primary : "#005B8E" }}
            >
              {fmt(total)}
            </span>
          </div>
        </Card>

        {/* Avertissement */}
        <div className="bg-brand-success-light border border-brand-success-border rounded-[10px] p-2.5 flex gap-1.5 mb-3">
          <i className="fa fa-shield-alt text-brand-success text-[11px] mt-px flex-shrink-0" />
          <p className="text-[11px] text-green-800 leading-relaxed">
            Vérifiez les deux numéros avant de confirmer. Cette opération est irréversible.
          </p>
        </div>

        <Button fullWidth variant="success" onClick={handleConfirm} className="mb-1.5">
          <i className="fa fa-check" /> &nbsp;Confirmer et payer
        </Button>
        <Button fullWidth variant="danger" onClick={() => navigate(from || "/")}>
          Annuler
        </Button>
      </div>
    </div>
  );
}
